import { useTranslation } from 'react-i18next'
import { ReportChart } from '@/components/relatorios/ReportChart'
import { ReportKpiCard } from '@/components/relatorios/ReportKpiCard'
import { ReportShell } from '@/components/relatorios/ReportShell'
import { ReportTable } from '@/components/relatorios/ReportTable'
import { exportCsv, formatDuration, formatPercent } from '@/components/relatorios/reportUtils'
import { useResponseTimeReport } from '@/hooks/reports/reportHooks'
import { useReportDateRange } from '@/hooks/reports/useReportDateRange'

export default function TempoRespostaReport() {
  const { t } = useTranslation('reports')
  const [range, setRange] = useReportDateRange()
  const report = useResponseTimeReport(range)
  const total = report.data.reduce((sum, row) => sum + row.responses, 0)
  const weightedAverage = total
    ? report.data.reduce((sum, row) => sum + row.averageSeconds * row.responses, 0) / total
    : 0

  return (
    <ReportShell
      title={t('responseTime.title')}
      description={t('responseTime.description')}
      {...range}
      onRangeChange={setRange}
      loading={report.loading}
      error={report.error}
      empty={total === 0}
      onRetry={report.refetch}
      onExport={() => exportCsv('tempo-resposta', report.data, [
        { label: t('fields.seller'), value: (row) => row.seller },
        { label: t('fields.responses'), value: (row) => row.responses },
        { label: t('fields.averageSeconds'), value: (row) => Math.round(row.averageSeconds) },
        { label: t('fields.medianSeconds'), value: (row) => Math.round(row.medianSeconds) },
        { label: t('fields.minSeconds'), value: (row) => Math.round(row.minSeconds) },
        { label: t('fields.maxSeconds'), value: (row) => Math.round(row.maxSeconds) },
        { label: t('fields.underFivePct'), value: (row) => row.underFiveMinutesPercentage.toFixed(1).replace('.', ',') },
      ])}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReportKpiCard label={t('responseTime.kpiResponses')} value={total} />
        <ReportKpiCard label={t('responseTime.kpiAverage')} value={formatDuration(weightedAverage)} />
      </div>
      <ReportChart
        type="bar"
        data={report.data.map((row) => ({ ...row, averageMinutes: row.averageSeconds / 60 }))}
        xKey="seller"
        series={[{ key: 'averageMinutes', label: t('fields.averageMinutes'), color: 'var(--primary)' }]}
      />
      <ReportTable
        rows={report.data.filter((row) => row.responses > 0)}
        rowKey={(row) => row.sellerId}
        columns={[
          { key: 'seller', label: t('fields.seller'), render: (row) => row.seller },
          { key: 'responses', label: t('fields.responses'), align: 'right', render: (row) => row.responses },
          { key: 'average', label: t('fields.average'), align: 'right', render: (row) => formatDuration(row.averageSeconds) },
          { key: 'median', label: t('fields.median'), align: 'right', render: (row) => formatDuration(row.medianSeconds) },
          { key: 'min', label: t('fields.min'), align: 'right', render: (row) => formatDuration(row.minSeconds) },
          { key: 'max', label: t('fields.max'), align: 'right', render: (row) => formatDuration(row.maxSeconds) },
          { key: 'under5', label: t('fields.underFive'), align: 'right', render: (row) => formatPercent(row.underFiveMinutesPercentage) },
        ]}
      />
    </ReportShell>
  )
}
